import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import { RequireAuth } from "../components/guards/RequireAuth";
import { FullPageSpinner } from "../components/patterns/FullPageSpinner";

/**
 * The route tree.
 *
 * WHY THE SHELLS ARE LAZY
 * -----------------------
 * A student on campus wifi should never download the registrar's screens. Each
 * shell is a code-split boundary, so the bundle a user fetches is the bundle
 * their role can actually reach. This is the single largest performance
 * decision in the application and it is cheap only if it is made now — routes
 * added later inherit the split for free.
 *
 * WHY `/` IS A PAGE AND NOT A REDIRECT
 * ------------------------------------
 * `/` is the address on the prospectus, the fee notice and every email. It has
 * to answer a question for someone who is not signed in — a guardian, an
 * applicant, someone verifying a receipt — and a bounce to `/login` answers
 * none of them. See `features/public/landing-page.tsx`.
 */

const LandingPage = lazy(() => import("../features/public/landing-page"));
const LoginPage = lazy(() => import("../features/auth/login-page"));
const AcceptInvitationPage = lazy(() => import("../features/auth/accept-invitation-page"));
const ChangePasswordPage = lazy(() => import("../features/auth/change-password-page"));
const ForgotPasswordPage = lazy(() => import("../features/auth/forgot-password-page"));
const ResetPasswordPage = lazy(() => import("../features/auth/reset-password-page"));
const StaffShell = lazy(() => import("../shells/staff-shell"));
const VerifyTranscriptPage = lazy(() => import("../features/transcripts/public-transcript-pages").then((m) => ({ default: m.VerifyTranscriptPage })));
const DeliveredTranscriptPage = lazy(() => import("../features/transcripts/public-transcript-pages").then((m) => ({ default: m.DeliveredTranscriptPage })));
const StudentShell = lazy(() => import("../shells/student-shell"));
const GuardianShell = lazy(() => import("../shells/guardian-shell"));

/**
 * The application's routes.
 *
 * @returns The route tree
 */
export function AppRoutes() {
    return (
        <Suspense fallback={<FullPageSpinner />}>
            <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />

                {/* Unguarded, and it MUST be. Whoever follows an invitation
                    link has no credentials — obtaining them is what the page
                    does. Putting this under /staff or /guardian would bounce
                    every invitee to a sign-in form they cannot pass. */}
                <Route path="/accept-invitation/:kind" element={<AcceptInvitationPage />} />

                {/* Unguarded for the same reason: the person here has lost
                    the password that would get them past a guard. The reset
                    email links to /reset-password, built by ms-xpsims-auth
                    from FRONTEND_URL — the path must not move. */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Outside the shells, and outside RequireAuth, deliberately.
                    An account that owes a password change is refused by the
                    gateway everywhere else, so a shell around this screen would
                    render navigation to a platform that answers nothing — and
                    putting it behind RequireAuth would make the guard that
                    sends people here also the guard that blocks them from
                    arriving. The page checks the session itself. */}
                <Route path="/change-password" element={<ChangePasswordPage />} />

                {/* Unguarded: whoever checks a transcript, or opens one sent to
                    their institution, has no account here. The addresses are
                    printed on paper and sent in email — they must not move. */}
                <Route path="/verify/transcript" element={<VerifyTranscriptPage />} />
                <Route path="/verify/transcript/:code" element={<VerifyTranscriptPage />} />
                <Route path="/transcripts/view/:token" element={<DeliveredTranscriptPage />} />

                <Route
                    path="/staff/*"
                    element={
                        <RequireAuth userType="staff">
                            <StaffShell />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/portal/*"
                    element={
                        <RequireAuth userType="student">
                            <StudentShell />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/guardian/*"
                    element={
                        <RequireAuth userType="guardian">
                            <GuardianShell />
                        </RequireAuth>
                    }
                />

                {/* Anything unrecognised lands on the front door rather than a
                    404 page. A stale bookmark from the legacy portal is the
                    common case, and the useful answer to it is the page with
                    links to everything. */}
                <Route path="*" element={<LandingPage />} />
            </Routes>
        </Suspense>
    );
}
